import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, Globe, Users, Leaf, FileText, Download } from 'lucide-react';

const LandingPage: React.FC = () => {
  const navigate = useNavigate();

  // Team members with profile images
  const team = [
    { name: 'Pravinkumar Menghani', role: 'Founder', image: '/images/Pravin.JPG' },
    { name: 'Pankaj Kejriwal', role: 'Co-Founder', image: '/images/Pankaj.jpeg' },
    { name: 'Purshottam Assudani', role: 'Co-Founder', image: '/images/Purshottam.jpeg' },
    { name: 'Qais Poonawala', role: 'Co-Founder', image: '/images/Qais.jpeg' }
  ];

  // Partners
  const partners = [
    { name: 'NxES Africa', description: 'Next Energy Solutions', image: '/images/NxES.JPG', url: 'https://nxesafrica.com/' },
    { name: 'NxNANO AB', description: 'Advanced Materials Innovation', image: '/images/NxES.JPG', url: 'https://nxnano.one/' },
    { name: 'EXCEED Solutions', description: 'Technology & Innovation Partner', image: '/images/Exceed-Solutions.png', url: '#' },
    { name: 'David Johnson Kazi', description: 'Strategic Partner - EXCEED Solutions', image: '/images/David.jpg', url: '#' },
    { name: 'Nils af Winklerfelt', description: 'Strategic Advisor', image: '/images/Nils.png', url: 'https://nxnano.one/' }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-green-50">
      {/* Header */}
      <header className="bg-white shadow-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between flex-wrap gap-4">
            {/* Left: NxES AI Climate Control Platform */}
            <div className="flex items-center gap-4">
              <img 
                src="/images/NxES.JPG" 
                alt="NxES" 
                className="h-16 w-16 rounded-lg object-contain shadow-md"
              />
              <div>
                <h1 className="text-2xl font-bold text-gray-900">NxES AI Climate Control Platform</h1>
                <p className="text-sm text-gray-600">Industrial Decarbonization Solution</p>
              </div>
            </div>

            {/* Right: Partner logos */}
            <div className="flex items-center gap-6 flex-wrap">
              <a 
                href="https://cloudcognoscente.com/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="flex items-center gap-2 hover:opacity-80 transition-opacity"
              >
                <img 
                  src="/images/logo.jpg" 
                  alt="Cloudcognoscente" 
                  className="h-12 w-12 rounded-lg object-cover"
                />
                <span className="text-sm font-medium text-gray-700">Cloudcognoscente</span>
              </a>
              <a 
                href="https://nxesafrica.com/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="flex items-center gap-2 hover:opacity-80 transition-opacity"
              >
                <img 
                  src="/images/NxES.JPG" 
                  alt="NxES Africa" 
                  className="h-12 w-12 rounded-lg object-contain"
                />
                <span className="text-sm font-medium text-gray-700">NxES Africa</span>
              </a>
              <a 
                href="https://nxnano.one/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="flex items-center gap-2 hover:opacity-80 transition-opacity"
              >
                <img 
                  src="/images/NxES.JPG" 
                  alt="NxNANO AB" 
                  className="h-12 w-12 rounded-lg object-contain"
                />
                <span className="text-sm font-medium text-gray-700">NxNANO AB</span>
              </a>
              <div className="flex items-center gap-2">
                <img 
                  src="/images/Exceed-Solutions.png" 
                  alt="EXCEED Solutions" 
                  className="h-12 w-12 rounded-lg object-contain"
                />
                <span className="text-sm font-medium text-gray-700">EXCEED Solutions</span>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 text-center">
        <div className="flex justify-center mb-6">
          <Leaf className="h-16 w-16 text-green-600" />
        </div>
        <h2 className="text-5xl font-bold text-gray-900 mb-6">
          NxES AI Climate Control Platform
        </h2>
        <p className="text-xl text-gray-600 mb-8 max-w-3xl mx-auto">
          Revolutionizing industrial decarbonization for steel and cement plants with 
          AI-powered monitoring, reporting, and verification
        </p>
        <button
          onClick={() => navigate('/login')}
          className="inline-flex items-center gap-2 px-8 py-4 bg-blue-600 text-white text-lg font-semibold rounded-lg hover:bg-blue-700 transition-colors shadow-lg"
        >
          Launch Platform
          <ArrowRight size={24} />
        </button>
      </section>

      {/* Documentation Section */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 bg-gradient-to-r from-blue-50 to-green-50 rounded-2xl mb-16">
        <div className="text-center mb-12">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">Platform Documentation</h2>
          <p className="text-gray-600 text-lg">Everything you need to understand and implement the platform</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
          {/* Quick Start Guide */}
          <div className="bg-white p-8 rounded-xl shadow-lg hover:shadow-xl transition-shadow">
            <div className="flex items-center gap-3 mb-4">
              <div className="bg-blue-100 p-3 rounded-lg">
                <FileText className="text-blue-600" size={32} />
              </div>
              <div>
                <h3 className="text-2xl font-bold text-gray-900">Quick Start Guide</h3>
                <p className="text-sm text-gray-500">For Managers & Decision-Makers</p>
              </div>
            </div>
            <p className="text-gray-600 mb-6">
              <strong>NxES Climate Control – What It Does and Why It Matters</strong><br/>
              A concise introduction explaining the platform's purpose, strategic value, and relevance for cement, steel, and waste systems.
            </p>
            <ul className="text-gray-600 mb-6 space-y-2">
              <li>✓ Platform purpose and capabilities</li>
              <li>✓ Strategic business value</li>
              <li>✓ ROI and impact metrics</li>
              <li>✓ Next steps for implementation</li>
            </ul>
            <a
              href="/docs/quick-start-guide.html"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-6 py-3 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 transition-colors w-full justify-center"
            >
              <FileText size={20} />
              View Guide (1 page)
            </a>
          </div>

          {/* System Overview */}
          <div className="bg-white p-8 rounded-xl shadow-lg hover:shadow-xl transition-shadow">
            <div className="flex items-center gap-3 mb-4">
              <div className="bg-green-100 p-3 rounded-lg">
                <FileText className="text-green-600" size={32} />
              </div>
              <div>
                <h3 className="text-2xl font-bold text-gray-900">System Overview</h3>
                <p className="text-sm text-gray-500">For Technical Teams & Partners</p>
              </div>
            </div>
            <p className="text-gray-600 mb-6">
              <strong>How the Platform Works – Modules, Data Flows, Adaptability</strong><br/>
              Technical architecture, MRV engine, digital twin, AI assistant, scenario modeling, and adaptation framework.
            </p>
            <ul className="text-gray-600 mb-6 space-y-2">
              <li>✓ System architecture and modules</li>
              <li>✓ Data integration and flows</li>
              <li>✓ Adaptability for any industry</li>
              <li>✓ Implementation timeline</li>
            </ul>
            <a
              href="/docs/system-overview.html"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-6 py-3 bg-green-600 text-white font-semibold rounded-lg hover:bg-green-700 transition-colors w-full justify-center"
            >
              <FileText size={20} />
              View Overview (2-3 pages)
            </a>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          <div className="bg-white p-6 rounded-lg shadow-md">
            <div className="bg-blue-100 w-12 h-12 rounded-lg flex items-center justify-center mb-4">
              <Leaf className="text-blue-600" size={24} />
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-2">Real-time Monitoring</h3>
            <p className="text-gray-600">
              IoT sensor integration and continuous emission tracking across all facilities
            </p>
          </div>

          <div className="bg-white p-6 rounded-lg shadow-md">
            <div className="bg-green-100 w-12 h-12 rounded-lg flex items-center justify-center mb-4">
              <Globe className="text-green-600" size={24} />
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-2">Carbon Credit Trading</h3>
            <p className="text-gray-600">
              Article 6 compliant carbon credit generation and international partnerships
            </p>
          </div>

          <div className="bg-white p-6 rounded-lg shadow-md">
            <div className="bg-purple-100 w-12 h-12 rounded-lg flex items-center justify-center mb-4">
              <Users className="text-purple-600" size={24} />
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-2">AI-Powered Insights</h3>
            <p className="text-gray-600">
              GPT-4o driven analysis, predictions, and optimization recommendations
            </p>
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 bg-white">
        <div className="text-center mb-12">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">Our Team</h2>
          <p className="text-gray-600 text-lg">Meet the experts behind the platform</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {team.map((member) => (
            <div key={member.name} className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow border border-gray-100">
              <div className="aspect-square bg-gradient-to-br from-blue-50 to-green-50 flex items-center justify-center p-4">
                <img 
                  src={member.image} 
                  alt={member.name}
                  className="w-full h-full object-cover rounded-lg"
                  onError={(e) => {
                    const initials = member.name.split(' ').map(n => n[0]).join('');
                    e.currentTarget.src = `data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="300" height="300"><rect fill="%234F46E5" width="300" height="300"/><text x="150" y="180" text-anchor="middle" fill="white" font-size="80" font-weight="bold">${initials}</text></svg>`;
                  }}
                />
              </div>
              <div className="p-6 text-center">
                <h3 className="text-xl font-bold text-gray-900">{member.name}</h3>
                <p className="text-blue-600 font-medium mt-1">{member.role}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Partners Section */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 bg-gradient-to-br from-blue-50 to-green-50">
        <div className="text-center mb-12">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">Our Partners</h2>
          <p className="text-gray-600 text-lg">Strategic partnerships driving climate innovation</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
          {partners.map((partner) => (
            <a 
              key={partner.name}
              href={partner.url}
              target="_blank"
              rel="noopener noreferrer"
              className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-all hover:scale-105 border border-gray-100"
            >
              <div className="aspect-square bg-white flex items-center justify-center p-6">
                <img 
                  src={partner.image} 
                  alt={partner.name}
                  className="w-full h-full object-contain"
                />
              </div>
              <div className="p-6 text-center">
                <h3 className="text-xl font-bold text-gray-900">{partner.name}</h3>
                <p className="text-gray-600 mt-1">{partner.description}</p>
              </div>
            </a>
          ))}
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-8 mt-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-gray-400">
            © 2025 Cloudcognoscente. All rights reserved.
          </p>
          <a 
            href="https://cloudcognoscente.com/" 
            target="_blank" 
            rel="noopener noreferrer"
            className="text-blue-400 hover:text-blue-300 mt-2 inline-block"
          >
            cloudcognoscente.com
          </a>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
